/* eslint-disable max-len */
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { FroalaEditorType } from '@shared/enums/FroalaEditorType.enum';
import { InvitedeskApiService } from '../api/legacy/invitedesk-api.service';
import { ErrorResponse, SuccessResponse } from '../api/legacy/response';
import { IAdvancedTemplateModel } from '../api/dashboard/advanced-templates-service/advanced-templates.model';

@Injectable()
export class FroalaService {

  public getOptions(
    translation: TranslateService,
    invitedeskApi: InvitedeskApiService,
    fullEditor = false,
    advancedTemplate: IAdvancedTemplateModel = null,
    hasImageUpload = false,
    hasVideo = false,
    hasCustomVars = false,
    hasCustomButtons = false,
    customFonts = [],
    charCounterCount = false,
    charCounterMax: number | null = null
  ): Record<string, unknown> {
    // Advanced template colors and fonts
    const templateColors = [];

    if (advancedTemplate) {
      templateColors.push(
        advancedTemplate.backgroundColor,
        advancedTemplate.contentColor,
        advancedTemplate.borderColor,
        advancedTemplate.headerSetting.headerContentSetting.backgroundColor,
        advancedTemplate.headerSetting.headerTextSetting.backgroundColor,
        advancedTemplate.bannerSetting.backgroundColor,
        advancedTemplate.contentSetting.contentBlockSetting.backgroundColor,
        advancedTemplate.contentSetting.contentBlockSetting.textColor,
        advancedTemplate.buttonSetting.buttonFullSetting.buttonColor.regularBackgroundColor,
        advancedTemplate.buttonSetting.buttonFullSetting.buttonColor.regularTextColor,
        advancedTemplate.buttonSetting.buttonFullSetting.buttonColor.regularBorderColor
      );
    }

    // Default colors
    let colors = [
      '#61BD6D', '#1ABC9C', '#54ACD2', '#2C82C9', '#9365B8', '#475577', '#CCCCCC',
      '#41A85F', '#00A885', '#3D8EB9', '#2969B0', '#553982', '#28324E', '#000000',
      '#F7DA64', '#FBA026', '#EB6B56', '#E25041', '#A38F84', '#EFEFEF', '#FFFFFF',
      '#FAC51C', '#F37934', '#D14841', '#B8312F', '#7C706B', '#D1D5D8', 'REMOVE'
    ];

    if (templateColors) {
      colors = templateColors.concat(colors);
    }

    // Default fonts
    let fonts = {
      'Gothic A1': 'Gothic A1',
      'Lato': 'Lato',
      'Montserrat': 'Montserrat',
      'Open Sans': 'Open Sans',
      'PT Sans': 'PT Sans',
      'Raleway': 'Raleway',
      'Roboto': 'Roboto',
      'Source Sans Pro': 'Source Sans Pro',
      'Arial': 'Arial',
      'Georgia': 'Georgia',
      'Helvetica': 'Helvetica',
      'Lucida': 'Lucida',
      'Tahoma': 'Tahoma',
      'Times New Roman': 'Times New Roman',
      'Trebuchet MS': 'Trebuchet MS',
      'Verdana': 'Verdana'
    };

    customFonts?.forEach(customFont => {
      const customFontList = {
        [customFont]: customFont
      };
      fonts = {...customFontList, ...fonts};
    });

    // Toolbar buttons
    const toolbarTextButtons = ['fontFamily', 'fontSize', 'bold', 'italic', 'underline', 'strikeThrough', 'subscript', 'superscript', 'textColor', 'backgroundColor', 'inlineClass', 'inlineStyle', 'clearFormatting'];
    const toolbarParagraphButtons = ['align', 'formatOL', 'formatUL', 'paragraphFormat', 'paragraphStyle', 'lineHeight', 'outdent', 'indent'];
    const toolbarRichButtons = ['insertLink', 'insertImage', 'insertTable', 'specialCharacters', 'insertHR'];
    const toolbarMiscButtons = ['undo', 'redo', 'html'];

    const limitedToolbarButtons = {
      'moreText': {
        'buttons': [],
        'buttonsVisible': 0,
        'align': 'left'
      },
      'moreParagraph': {
        'buttons': [],
        'buttonsVisible': 0,
        'align': 'left'
      },
      'moreRich': {
        'buttons': [],
        'buttonsVisible': 0,
        'align': 'left'
      },
      'moreMisc': {
        'buttons': ['undo', 'redo'],
        'buttonsVisible': 3
      }
    };
    const fullToolbarButtons = {
      'moreText': {
        'buttons': toolbarTextButtons,
        'buttonsVisible': 2,
        'align': 'left'
      },
      'moreParagraph': {
        'buttons': toolbarParagraphButtons,
        'buttonsVisible': 0,
        'align': 'left'
      },
      'moreRich': {
        'buttons': toolbarRichButtons,
        'buttonsVisible': 0,
        'align': 'left'
      },
      'moreMisc': {
        'buttons': toolbarMiscButtons,
        'buttonsVisible': 5,
        'align': 'left'
      }
    };
    const fullToolbarButtonsMD = {
      'moreText': {
        'buttons': toolbarTextButtons,
        'buttonsVisible': 2
      },
      'moreParagraph': {
        'buttons': toolbarParagraphButtons,
        'buttonsVisible': 0
      },
      'moreRich': {
        'buttons': toolbarRichButtons,
        'buttonsVisible': 0
      },
      'moreMisc': {
        'buttons': toolbarMiscButtons,
        'buttonsVisible': 0
      }
    };
    const fullToolbarButtonsSM = {
      'moreText': {
        'buttons': toolbarTextButtons,
        'buttonsVisible': 2
      },
      'moreParagraph': {
        'buttons': toolbarParagraphButtons,
        'buttonsVisible': 0
      },
      'moreRich': {
        'buttons': toolbarRichButtons,
        'buttonsVisible': 0
      },
      'moreMisc': {
        'buttons': toolbarMiscButtons,
        'buttonsVisible': 0
      }
    };
    const fullToolbarButtonsXS = {
      'moreText': {
        'buttons': toolbarTextButtons,
        'buttonsVisible': 0
      },
      'moreParagraph': {
        'buttons': toolbarParagraphButtons,
        'buttonsVisible': 0
      },
      'moreRich': {
        'buttons': toolbarRichButtons,
        'buttonsVisible': 0
      },
      'moreMisc': {
        'buttons': toolbarMiscButtons,
        'buttonsVisible': 0
      }
    };

    // Default options that apply to all editors
    const defaultOptions = {
      key: `${environment.froala}`,
      attribution: false,
      charCounterCount: false,
      charCounterMax: null,
      enter: `$.FroalaEditor.ENTER_BR`,
      tabSpaces: 4,
      zIndex: 9990,
      placeholderText: translation.instant('FROALA.PLACEHOLDER'),
      language: translation.instant('FROALA.LANGUAGE'),
      toolbarSticky: false,
      videoUpload: false,
      quickInsertEnabled: false,
      useClasses: false,
      toolbarButtons: fullEditor ? fullToolbarButtons : limitedToolbarButtons,
      toolbarButtonsMD: fullEditor ? fullToolbarButtonsMD : limitedToolbarButtons,
      toolbarButtonsSM: fullEditor ? fullToolbarButtonsSM : limitedToolbarButtons,
      toolbarButtonsXS: fullEditor ? fullToolbarButtonsXS : limitedToolbarButtons,
      fontFamily: fonts,
      fontFamilyDefaultSelection: advancedTemplate ? advancedTemplate?.contentSetting?.contentBlockSetting?.fontFamily : 'Arial',
      fontFamilySelection: true,
      fontSizeSelection: true,
      colorsBackground: colors,
      colorsText: colors,
      colorsStep: 7,
      htmlRemoveTags: ['script']
    };

    // Default options for editors with images
    const imageOptions = {
      imageEditButtons: [
        'imageReplace', 'imageAlign', 'imageRemove', '|',
        'imageLink', 'linkOpen', 'linkEdit', 'linkRemove', '-',
        'imageDisplay', 'imageStyle', 'imageAlt', 'imageSize'
      ],
      imageInsertButtons: [
        'imageBack', '|',
        'imageUpload', 'imageByURL'
      ],
      imageDefaultDisplay: 'inline',
      imageDefaultAlign: 'left',
      imageMultipleStyles: false,
      imagePasteProcess: true,
      imageOutputSize: true,
      imageMaxSize: 2 * 1024 * 1024,
      imageAllowedTypes: ['jpeg', 'jpg', 'png', 'gif'],
      events: {
        'image.beforeUpload'(images) {
            if (images.length) {
                invitedeskApi.uploads
                    .uploadImage(images[0])
                    .subscribe(
                    (x: SuccessResponse<string>) => {
                        if (!!x.data) {
                            this.image.insert(x.data, null, null, this.image.get());
                        } else {
                            console.error('uploadImage', x?.messages[0]?.text);
                        }
                    },
                    (error: ErrorResponse) => {
                        console.error('uploadImage', error);
                    });
            }
            return false;
        },
        // 'table.inserted'(table) {
        //   table.border = '0';
        //       table.cellPadding  = '0';
        //       table.cellSpacing= '0';
        // }
      }
    };

    let froalaParams = defaultOptions;

    if (fullEditor) {
      froalaParams.toolbarButtons = fullToolbarButtons;
      froalaParams = {...froalaParams,
        ...{
          tableStyles: {
            'fr-dashed-borders': translation.instant('FROALA.BORDERS.DASHED'),
            'fr-no-borders': translation.instant('FROALA.BORDERS.NONE')
          }
        }
      };
    }

    if (hasImageUpload) {
      froalaParams = {...froalaParams, ...imageOptions};
    }

    if (charCounterCount) {
        froalaParams = { ...froalaParams, charCounterCount: true };

        if (charCounterMax) {
            froalaParams = { ...froalaParams, charCounterMax };
        }
    }

    if (hasVideo) {
      froalaParams.toolbarButtons.moreRich.buttons.push('insertVideo');
    }

    if (hasCustomVars) {
      froalaParams.toolbarButtons.moreMisc.buttons.push('custom_vars');
    }

    if (hasCustomButtons) {
      froalaParams.toolbarButtons.moreMisc.buttons.push('custom_buttons');
    }

    return froalaParams;
  }

  public getCustomVars(translation: TranslateService, editorType: FroalaEditorType, hasLastNamePrefixPermission: boolean): Record<string, unknown> {
    const defaultOptions = {
      '#EventName': translation.instant('FROALA.CUSTOMVARS.EVENTNAME'),
      '#FirstName': translation.instant('FROALA.CUSTOMVARS.FIRSTNAME'),
      ...(hasLastNamePrefixPermission ? {
        '#LastNamePrefix': translation.instant('FROALA.CUSTOMVARS.LASTNAMEPREFIX'),
      } : {}),
      '#LastName': translation.instant('FROALA.CUSTOMVARS.LASTNAME'),
      '#Email': translation.instant('FROALA.CUSTOMVARS.EMAIL'),
      '#Mobile': translation.instant('FROALA.CUSTOMVARS.MOBILE'),
      '#Phone': translation.instant('FROALA.CUSTOMVARS.PHONE'),
      '#Salutation': translation.instant('FROALA.CUSTOMVARS.SALUTATION'),
      '#CompanyName': translation.instant('FROALA.CUSTOMVARS.COMPANYNAME'),
      '#CustomerNumber': translation.instant('FROALA.CUSTOMVARS.CUSTOMERNUMBER'),
      '#AccessCode': translation.instant('FROALA.CUSTOMVARS.ACCESSCODE'),
      '#JobTitle': translation.instant('FROALA.CUSTOMVARS.JOBTITLE'),
      '#RegistrationUrl': translation.instant('FROALA.CUSTOMVARS.REGISTRATIONURL'),
    };
    const defaultOptionsUser = {
      '#UserFirstName': translation.instant('FROALA.CUSTOMVARS.USER.FIRSTNAME'),
      '#UserLastName': translation.instant('FROALA.CUSTOMVARS.USER.LASTNAME'),
      '#UserEmail': translation.instant('FROALA.CUSTOMVARS.USER.EMAIL'),
      '#UserJobTitle': translation.instant('FROALA.CUSTOMVARS.USER.JOBTITLE'),
      '#UserMobile': translation.instant('FROALA.CUSTOMVARS.USER.MOBILE'),
      '#UserPhone': translation.instant('FROALA.CUSTOMVARS.USER.PHONE'),
      '#UserCompanyName': translation.instant('FROALA.CUSTOMVARS.USER.COMPANYNAME'),
      '#UserCRMID': translation.instant('FROALA.CUSTOMVARS.USER.CRMID')
    };
    let options = {};

    switch (editorType) {
      case FroalaEditorType.All:
        options = {...defaultOptions, ...defaultOptionsUser,
          ...{'#VenueName': translation.instant('FROALA.CUSTOMVARS.VENUENAME')},
          ...{'#VenueDate': translation.instant('FROALA.CUSTOMVARS.VENUEDATE')},
          ...{'#VenueStartHour': translation.instant('FROALA.CUSTOMVARS.VENUESTARTHOUR')},
          ...{'#VenueEndHour': translation.instant('FROALA.CUSTOMVARS.VENUEENDHOUR')},
          ...{'#LineUprAppUrl': translation.instant('FROALA.CUSTOMVARS.LINEUPRAPPURL')}
        };
        break;
      case FroalaEditorType.ThankYou:
        // Mirrors the All set, plus #Documents (only Thank-You and Ad-Hoc expose #Documents).
        options = {...defaultOptions, ...defaultOptionsUser,
          ...{'#VenueName': translation.instant('FROALA.CUSTOMVARS.VENUENAME')},
          ...{'#VenueDate': translation.instant('FROALA.CUSTOMVARS.VENUEDATE')},
          ...{'#VenueStartHour': translation.instant('FROALA.CUSTOMVARS.VENUESTARTHOUR')},
          ...{'#VenueEndHour': translation.instant('FROALA.CUSTOMVARS.VENUEENDHOUR')},
          ...{'#LineUprAppUrl': translation.instant('FROALA.CUSTOMVARS.LINEUPRAPPURL')},
          ...{'#Documents': translation.instant('FROALA.CUSTOMVARS.DOCUMENTS')}
        };
        break;
      case FroalaEditorType.Venue:
        options = {...defaultOptions, ...defaultOptionsUser,
          ...{'#VenueName': translation.instant('FROALA.CUSTOMVARS.VENUENAME')},
          ...{'#VenueDate': translation.instant('FROALA.CUSTOMVARS.VENUEDATE')},
          ...{'#VenueStartHour': translation.instant('FROALA.CUSTOMVARS.VENUESTARTHOUR')},
          ...{'#VenueEndHour': translation.instant('FROALA.CUSTOMVARS.VENUEENDHOUR')},
          ...{'#LineUprAppUrl': translation.instant('FROALA.CUSTOMVARS.LINEUPRAPPURL')},
          ...{'#NumberOfAttendees': translation.instant('FROALA.CUSTOMVARS.NUMBER.OF.ATTENDEES')}
        };
        break;
      case FroalaEditorType.AdHoc:
        options = {...defaultOptions, ...defaultOptionsUser,
          ...{'#VenueName': translation.instant('FROALA.CUSTOMVARS.VENUENAME')},
          ...{'#VenueDate': translation.instant('FROALA.CUSTOMVARS.VENUEDATE')},
          ...{'#VenueStartHour': translation.instant('FROALA.CUSTOMVARS.VENUESTARTHOUR')},
          ...{'#VenueEndHour': translation.instant('FROALA.CUSTOMVARS.VENUEENDHOUR')},
          ...{'#LineUprAppUrl': translation.instant('FROALA.CUSTOMVARS.LINEUPRAPPURL')},
          ...{'#NumberOfAttendees': translation.instant('FROALA.CUSTOMVARS.NUMBER.OF.ATTENDEES')},
          ...{'#Documents': translation.instant('FROALA.CUSTOMVARS.DOCUMENTS')}
        };
        break;
      case FroalaEditorType.Rsvp:
        options = {...defaultOptions, ...defaultOptionsUser,
          ...{'#RsvpDate': translation.instant('FROALA.CUSTOMVARS.RSVPDATE')}
        };
        break;
      case FroalaEditorType.PublicPage:
        options = {...defaultOptions,
          ...{'#PublicPageUrl': translation.instant('FROALA.CUSTOMVARS.PUBLICPAGEURL')}
        };
        break;
      case FroalaEditorType.ConfirmationMail:
        options = {...defaultOptions, ...defaultOptionsUser,
          ...{'#VenueName': translation.instant('FROALA.CUSTOMVARS.VENUENAME')},
          ...{'#VenueDate': translation.instant('FROALA.CUSTOMVARS.VENUEDATE')},
          ...{'#NumberOfAttendees': translation.instant('FROALA.CUSTOMVARS.NUMBER.OF.ATTENDEES')},
          ...{'#VenueStartHour': translation.instant('FROALA.CUSTOMVARS.VENUESTARTHOUR')},
          ...{'#VenueEndHour': translation.instant('FROALA.CUSTOMVARS.VENUEENDHOUR')},
          ...{'#LineUprAppUrl': translation.instant('FROALA.CUSTOMVARS.LINEUPRAPPURL')}
        };
        break;
        case FroalaEditorType.Document:
          options = {... defaultOptions,
            ...{'#VenueName': translation.instant('FROALA.CUSTOMVARS.VENUENAME')},
            ...{'#VenueDate': translation.instant('FROALA.CUSTOMVARS.VENUEDATE')},
            ...{'#VenueStartHour': translation.instant('FROALA.CUSTOMVARS.VENUESTARTHOUR')},
            ...{'#VenueEndHour': translation.instant('FROALA.CUSTOMVARS.VENUEENDHOUR')}
            };
    }

    const froalaParams = {
      title: translation.instant('FROALA.CUSTOMVARS.TITLE'),
      type: 'dropdown',
      focus: true,
      undo: true,
      refreshAfterCallback: true,
      options,
      callback(cmd, val) {
          this.html.insert(val);
          this.undo.saveStep();
      }
    };
    return froalaParams;
  }

  public removeVenueRelatedVars(froalaParams: Record<string, unknown>): Record<string, unknown> {
    // for multivenue eventmails, any venue related custom vars can not be used
    const optionsForMultiVenue = {};

    Object.keys(froalaParams.options).forEach(propertyName => {
      if (propertyName.startsWith('#Venue')) {
        // skip every property name that start with venue
        return;
      }
      optionsForMultiVenue[propertyName] = froalaParams.options[propertyName];
    });
    froalaParams.options = optionsForMultiVenue;
    return froalaParams;
  }

  public getCustomButtons(translation: TranslateService, host: any): Record<string, unknown> {
    const froalaParams = {
      title: translation.instant('FROALA.CUSTOMBUTTONS.TITLE'),
      type: 'dropdown',
      focus: true,
      undo: true,
      refreshAfterCallback: true,
      options: {
          'button_full': translation.instant('FROALA.CUSTOMBUTTONS.FULL'),
          'button_hollow': translation.instant('FROALA.CUSTOMBUTTONS.HOLLOW')
      },
      callback(cmd, buttonType) {
        this.html.insert(host.froalaService.getHTMLButton(
          buttonType,
          host.advancedTemplate.buttonSetting.buttonFullSetting.buttonColor.regularTextColor,
          host.advancedTemplate.buttonSetting.buttonFullSetting.buttonColor.regularBackgroundColor,
          host.advancedTemplate.buttonSetting.fontFamily,
          host.translation.instant('FROALA.CUSTOMBUTTONS.CONTENT')
        ));
        this.undo.saveStep();
      }
    };
    return froalaParams;
  }

  public getHTMLButton(buttonType: string, buttonTextColor: string, buttonBackgroundColor: string, contentFontfamily: string, content: string): string {
    let htmlButton = '';

    switch (buttonType) {
      // HTML for full button Froala
      case 'button_full':
        htmlButton =
          '<table border=\'0\' cellpadding=\'0\' cellspacing=\'0\' role=\'presentation\' style=\'border-collapse:separate;width:100%;line-height:100%;\'>' +
          '<tr>' +
          '<td align=\'center\' bgcolor=\'' + buttonBackgroundColor + '\' role=\'presentation\' style=\'border:1px solid ' + buttonBackgroundColor + ';border-radius:3px;cursor:auto;mso-padding-alt:12px 25px 12px 25px;background: ' + buttonBackgroundColor + ';padding:12px 25px 12px 25px;\' valign=\'middle\'>' +
          '<a target=\'_blank\' href=\'\' style=\"text-decoration:none;background:' + buttonBackgroundColor + ';color:' + buttonBackgroundColor + ';font-family:\'' + contentFontfamily + '\',Arial,sans-serif;font-size:13px;font-weight:normal;line-height:120%;text-transform:none;margin:0px;\">' +
          '<b>' +
          '<span style=\'color: ' + buttonTextColor + '\'>' +
          content +
          '</span>' +
          '</b>' +
          '</a>' +
          '</td>' +
          '</tr>' +
          '</table>'
        ;
        break;
      // HTML for hollow button Froala
      case 'button_hollow':
        htmlButton =
          '<table border=\'0\' cellpadding=\'0\' cellspacing=\'0\' role=\'presentation\' style=\'border-collapse:separate;width:100%;line-height:100%;\'>' +
          '<tr>' +
          '<td align=\'center\' bgcolor=\'' + buttonTextColor + '\' role=\'presentation\' style=\'width:100%;line-height:100%;border:1px solid ' + buttonBackgroundColor + ';border-radius:3px;cursor:auto;mso-padding-alt:12px 25px 12px 25px;background: ' + buttonTextColor + ';padding:12px 25px 12px 25px;\' valign=\'middle\'>' +
          '<a target=\'_blank\' href=\'\' style=\"text-decoration:none;background:' + buttonTextColor + ';color:' + buttonTextColor + ';font-family:\'' + contentFontfamily + '\',Arial,sans-serif;font-size:13px;font-weight:normal;line-height:120%;text-transform:none;margin:0px;\">' +
          '<b>' +
          '<span style=\'color: ' + buttonBackgroundColor + '\'>' +
          content +
          '</span>' +
          '</b>' +
          '</a>' +
          '</td>' +
          '</tr>' +
          '</table>'
        ;
        break;
    }

    return htmlButton;
  }
}
